import { MapPin, Loader2, X, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { DISTANCE_OPTIONS } from "@/lib/distance";
import type { GeocodingResult } from "@/lib/geocoding";
import { useZipGeocode } from "@/hooks/useZipGeocode";

interface ZipFieldProps {
  zipCode: string;
  onZipCodeChange: (zipCode: string) => void;
  onLocationChange: (location: GeocodingResult | null) => void;
  /** The place the current zip resolved to, echoed back as confirmation. */
  location: GeocodingResult | null;
  /** Larger type and spacing, for the nearby tab's full-width prompt. */
  size?: "default" | "large";
  autoFocus?: boolean;
  id?: string;
  className?: string;
  /**
   * The resolved town, echoed under the field. Turned off where something
   * next to it already names the place, so it is not said twice.
   */
  showConfirmation?: boolean;
}

/**
 * The zip code field itself, without the surrounding "Location" section.
 *
 * Split out because the nearby tab asks for a zip in the middle of the page
 * rather than in the filter sidebar, and both places have to drive the same
 * filter state.
 */
export function ZipField({
  zipCode,
  onZipCodeChange,
  onLocationChange,
  location,
  size = "default",
  autoFocus,
  id = "zipcode",
  className,
  showConfirmation = true,
}: ZipFieldProps) {
  const { isGeocoding, error } = useZipGeocode(zipCode, onLocationChange);
  const large = size === "large";

  const handleClear = () => {
    onZipCodeChange("");
    onLocationChange(null);
  };

  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={id} className="sr-only">
        Zip Code
      </Label>
      <div className="relative">
        <MapPin
          className={cn(
            "absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground",
            large ? "h-5 w-5" : "h-4 w-4"
          )}
        />
        <Input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="postal-code"
          autoFocus={autoFocus}
          value={zipCode}
          onChange={(e) => onZipCodeChange(e.target.value.replace(/\D/g, "").slice(0, 5))}
          placeholder="Enter zip code"
          className={cn(large ? "pl-11 pr-11 h-12 text-base" : "pl-9 pr-9")}
          maxLength={5}
        />
        {isGeocoding && (
          <Loader2
            className={cn(
              "absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground",
              large ? "h-5 w-5" : "h-4 w-4"
            )}
          />
        )}
        {!isGeocoding && zipCode && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear zip code"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className={large ? "h-5 w-5" : "h-4 w-4"} />
          </button>
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {/* Naming the town catches the typo that still geocodes — 02138 for
          02139 — which otherwise just looks like a quiet week for dancing. */}
      {showConfirmation && !error && !isGeocoding && location?.label && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Check className="h-3 w-3 text-emerald-500" />
          {location.label}
        </p>
      )}
    </div>
  );
}

/** The radius picker. Only meaningful once a zip has resolved. */
export function DistanceSelect({
  distance,
  onDistanceChange,
  id = "distance",
  className,
}: {
  distance: number;
  onDistanceChange: (distance: number) => void;
  id?: string;
  className?: string;
}) {
  return (
    <Select
      value={distance.toString()}
      onValueChange={(val) => onDistanceChange(parseInt(val, 10))}
    >
      <SelectTrigger id={id} className={className}>
        <SelectValue placeholder="Select distance" />
      </SelectTrigger>
      <SelectContent>
        {DISTANCE_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value.toString()}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface ZipCodeInputProps {
  zipCode: string;
  onZipCodeChange: (zipCode: string) => void;
  distance: number;
  onDistanceChange: (distance: number) => void;
  onLocationChange: (location: GeocodingResult | null) => void;
  location: GeocodingResult | null;
}

export function ZipCodeInput({
  zipCode,
  onZipCodeChange,
  distance,
  onDistanceChange,
  onLocationChange,
  location,
}: ZipCodeInputProps) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">Location</h3>
      <ZipField
        zipCode={zipCode}
        onZipCodeChange={onZipCodeChange}
        onLocationChange={onLocationChange}
        location={location}
      />

      {location && (
        <div className="space-y-2">
          <Label htmlFor="distance" className="text-xs text-muted-foreground">
            Within
          </Label>
          <DistanceSelect distance={distance} onDistanceChange={onDistanceChange} />
        </div>
      )}
    </div>
  );
}
